import { useEffect, useRef, useState, type DragEvent } from 'react'
import { FileText, Image as ImageIcon, Loader2, Paperclip, UploadCloud, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { filesFromClipboard } from '@/lib/clipboardFiles'
import { describeUploadFailure, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, uploadBlob } from '@/lib/upload'

/**
 * Supplementary images and files, uploaded before the thing they hang off exists.
 *
 * The two-path upload — straight to S3 where the bucket allows it, through our own API where
 * it does not — lives in `lib/upload`, because a profile photo takes the same route and the
 * two should not learn about a CORS rule separately.
 *
 * Two buttons, one list. An image IS a file; the split is about where people look for the
 * control, not about where the file goes, and the API stores them in one place for the same
 * reason. A screenshot added through "Attach files" is still shown as a screenshot.
 */

/** Re-exported: forms that quote the limit in their own hint text read it from here. */
export const MAX_ATTACHMENT_BYTES = MAX_UPLOAD_BYTES
export const MAX_ATTACHMENTS = 20

const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/heic'
const FILE_ACCEPT = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/zip',
  '.doc,.docx,.xls,.xlsx,.ppt,.pptx',
  'video/mp4',
  'video/quicktime',
].join(',')

/** What the form submits: the key the API minted, and the name to show. */
export interface AttachmentRef {
  key: string
  name: string
}

interface Pending {
  /** Local only — a row that is still uploading has no key yet. */
  localId: string
  name: string
  size: number
  contentType: string
  state: 'uploading' | 'done' | 'failed'
  key?: string
  error?: string
  /** A local thumbnail for a picture picked in this session. */
  preview?: string
}

const prettySize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`

const isImage = (contentType: string): boolean => contentType.startsWith('image/')

/** What an item already carries, as the API returns it. */
export interface ExistingAttachment {
  key: string
  name: string
  size: number
  contentType: string
}

export function AttachmentsField({
  onChange,
  existing,
  disabled = false,
  label = 'Attachments',
  imagesOnly = false,
  listenForPaste = false,
  onBusyChange,
}: {
  onChange: (next: AttachmentRef[]) => void
  /** Files already on the item, when editing. Seeded once; removing one drops it on save. */
  existing?: ExistingAttachment[]
  disabled?: boolean
  label?: string
  /** Photos only: no file picker, and the wording says so. */
  imagesOnly?: boolean
  /** Take pastes anywhere on the page — for a dialog whose only job is this field. */
  listenForPaste?: boolean
  /** True while any file is still going up, so a form can hold its submit button. */
  onBusyChange?: (busy: boolean) => void
}) {
  /*
   * Seeded once, from the initial render only.
   *
   * These rows start as `done` with their real key, so the field's value contains them from
   * the outset — an edit that touched nothing here must still submit the files that were
   * already there, because the API reads the list as the whole truth and would otherwise
   * take an untouched form as "remove them all".
   */
  const [rows, setRows] = useState<Pending[]>(() =>
    (existing ?? []).map((a) => ({
      localId: a.key,
      name: a.name,
      size: a.size,
      contentType: a.contentType,
      state: 'done' as const,
      key: a.key,
    })),
  )
  const imageInput = useRef<HTMLInputElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  const [draggingFiles, setDraggingFiles] = useState(false)
  // Paste goes to whichever zone the pointer is over or that holds focus, so two fields on one
  // page do not both take the same screenshot.
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const addRef = useRef<(files: ArrayLike<File> | null) => void>(() => {})
  const pasteActive = !disabled && (listenForPaste || hovered || focused)

  useEffect(() => {
    if (!pasteActive) return
    const onPaste = (event: ClipboardEvent) => {
      let files = filesFromClipboard(event.clipboardData)
      if (imagesOnly) files = files.filter((f) => f.type.startsWith('image/'))
      if (files.length === 0) return
      event.preventDefault()
      addRef.current(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [pasteActive, imagesOnly])

  const publish = (next: Pending[]) => {
    setRows(next)
    onBusyChange?.(next.some((r) => r.state === 'uploading'))
    // Only finished uploads are part of the form's value. A row still in flight, or one that
    // failed, must not be submitted as though the file were there.
    onChange(
      next
        .filter((r): r is Pending & { key: string } => r.state === 'done' && Boolean(r.key))
        .map((r) => ({ key: r.key, name: r.name })),
    )
  }

  async function upload(file: File, localId: string, current: Pending[]) {
    const patch = (change: Partial<Pending>) => {
      current = current.map((r) => (r.localId === localId ? { ...r, ...change } : r))
      publish(current)
    }

    try {
      patch({ state: 'done', key: await uploadBlob(file, file.name, file.type) })
    } catch (err) {
      patch({ state: 'failed', error: describeUploadFailure(err) })
    }
  }

  function add(files: ArrayLike<File> | null) {
    if (disabled || !files || files.length === 0) return
    const room = MAX_ATTACHMENTS - rows.length
    const chosen = Array.from(files).slice(0, Math.max(0, room))

    const next = [...rows]
    for (const file of chosen) {
      const localId = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`

      // Checked here so somebody is told before watching a progress bar fill, not after.
      if (imagesOnly && !file.type.startsWith('image/')) continue
      const tooBig = file.size > MAX_ATTACHMENT_BYTES
      const noType = !file.type
      const row: Pending = {
        localId,
        name: file.name,
        size: file.size,
        contentType: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        state: tooBig || noType ? 'failed' : 'uploading',
        error: tooBig
          ? `Larger than the ${MAX_UPLOAD_MB}MB limit.`
          : noType
            ? 'That kind of file cannot be attached.'
            : undefined,
      }
      next.push(row)
      if (row.state === 'uploading') void upload(file, localId, next)
    }
    publish(next)
  }
  useEffect(() => {
    addRef.current = add
  })

  const remove = (localId: string) => {
    const gone = rows.find((r) => r.localId === localId)
    if (gone?.preview) URL.revokeObjectURL(gone.preview)
    publish(rows.filter((r) => r.localId !== localId))
  }

  const full = rows.length >= MAX_ATTACHMENTS

  function hasFiles(event: DragEvent<HTMLElement>): boolean {
    return Array.from(event.dataTransfer.types).includes('Files')
  }

  function onDragEnter(event: DragEvent<HTMLDivElement>) {
    if (disabled || full || !hasFiles(event)) return
    event.preventDefault()
    dragDepth.current += 1
    setDraggingFiles(true)
  }

  function onDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return
    event.preventDefault()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDraggingFiles(false)
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    if (disabled || full || !hasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return
    event.preventDefault()
    dragDepth.current = 0
    setDraggingFiles(false)
    if (disabled || full) return
    add(event.dataTransfer.files)
  }

  return (
    <div className="grid gap-1.5">
      <Label>
        {label}
        <span className="text-text-muted"> — optional</span>
      </Label>

      <input
        ref={imageInput}
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={fileInput}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files)
          e.target.value = ''
        }}
      />

      <div
        tabIndex={disabled ? -1 : 0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={cn(
          'grid min-h-28 gap-3 rounded-lg border-2 border-dashed p-4 outline-none transition-colors dur-fast focus-visible:ring-2 focus-visible:ring-ring',
          draggingFiles ? 'border-brand bg-brand-soft/60' : 'border-border bg-surface-sunken/35 hover:border-border-strong',
          (disabled || full) && 'opacity-70',
        )}
        aria-label="Attachment drop zone"
      >
        <div className="flex items-center justify-center gap-2 text-center text-sm text-text-muted">
          <UploadCloud className={cn('size-5', draggingFiles && 'text-brand')} />
          <span>
            {draggingFiles
              ? imagesOnly ? 'Drop photos to add them' : 'Drop files to attach them'
              : full
                ? `Attachment limit reached (${MAX_ATTACHMENTS})`
                : imagesOnly
                  ? 'Drop photos here, paste with ⌘V / Ctrl+V, or choose them'
                  : 'Drop photos or files here, paste a screenshot with ⌘V / Ctrl+V, or choose them'}
          </span>
        </div>

        <div className="flex flex-wrap justify-center gap-1.5">
          <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={disabled || full}
          onClick={() => imageInput.current?.click()}
        >
          <ImageIcon className="size-3.5" />
          {imagesOnly ? 'Add photos' : 'Attach images'}
        </Button>
        {imagesOnly ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={disabled || full}
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip className="size-3.5" />
          Attach files
        </Button>
        )}
        </div>
      </div>

      {rows.some((r) => r.preview) && (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-2" aria-label="Photos">
          {rows
            .filter((r) => r.preview)
            .map((row) => (
              <li
                key={row.localId}
                className={cn(
                  'group relative aspect-square overflow-hidden rounded-md border bg-muted',
                  row.state === 'failed' ? 'border-danger/60' : 'border-border',
                )}
                title={row.state === 'failed' ? row.error : row.name}
              >
                <img src={row.preview} alt={row.name} className={cn('size-full object-cover', row.state !== 'done' && 'opacity-50')} />
                {row.state === 'uploading' ? (
                  <Loader2 className="absolute inset-0 m-auto size-4 animate-spin text-foreground" />
                ) : null}
                {row.state === 'failed' ? (
                  <span className="absolute inset-x-0 bottom-0 bg-danger/80 px-1 text-center text-micro text-white">Failed</span>
                ) : null}
                <button
                  type="button"
                  onClick={() => remove(row.localId)}
                  aria-label={`Remove ${row.name}`}
                  className="absolute right-0.5 top-0.5 grid size-5 place-items-center rounded-full bg-foreground/70 text-background opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <X className="size-3" />
                </button>
              </li>
            ))}
        </ul>
      )}

      {rows.some((r) => !r.preview) && (
        <ul className="grid gap-1">
          {rows.filter((r) => !r.preview).map((row) => (
            <li
              key={row.localId}
              className={cn(
                'flex items-center gap-2 rounded-md border px-2 py-1.5',
                row.state === 'failed' ? 'border-danger/40 bg-danger/5' : 'border-border',
              )}
            >
              {row.state === 'uploading' ? (
                <Loader2 className="size-3.5 shrink-0 animate-spin text-text-muted" />
              ) : isImage(row.contentType) ? (
                <ImageIcon className="size-3.5 shrink-0 text-text-muted" />
              ) : (
                <FileText className="size-3.5 shrink-0 text-text-muted" />
              )}

              <span className="min-w-0 flex-1 truncate text-xs" title={row.name}>
                {row.name}
              </span>

              <span className="shrink-0 text-tiny text-text-muted">
                {row.state === 'failed' ? row.error : prettySize(row.size)}
              </span>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                onClick={() => remove(row.localId)}
                aria-label={`Remove ${row.name}`}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-tiny text-text-muted">
        {full
          ? `That is all ${MAX_ATTACHMENTS}.`
          : `Screenshots, briefs, signed documents. Up to ${MAX_UPLOAD_MB}MB each.`}
      </p>
    </div>
  )
}

/** Only the uploads that finished. Use this when submitting. */
export const cleanAttachments = (refs: AttachmentRef[]): AttachmentRef[] =>
  refs.filter((r) => r.key && r.name)
